import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["value"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { NON_DIGITS, NON_DIGITS_GLOBAL, pipe, stripNonDigits } from '../../../../utils';
import { DATE_FULL_MASK_REGEX, DATE_MASK_CHARS_REGEX, DATE_MASK_USER_INPUT_REGEX, SANITIZE_DATE_REGEX } from './constants';

var conditionallyMaskDate = data => {
  var {
    inputAction,
    meta,
    selectionStart,
    value
  } = data;
  var dateLen = value.sterilized.length;
  var isInsertion = inputAction > 0;
  var isMonthPresentAndUserEnteredCharAtMask = dateLen === 2 && (selectionStart > 1 || meta.didZeroPad) && (isInsertion || meta.didUserEnterInvalidChar);

  if (dateLen > 2 || isMonthPresentAndUserEnteredCharAtMask || meta.didUserEnterMaskChar) {
    return maskDate(value.sterilized);
  }

  return value.sterilized;
};

var conditionallyZeroPadValue = _ref => {
  var {
    meta,
    value
  } = _ref;
  var {
    sterilized
  } = value;
  var firstDigit = parseInt(sterilized.charAt(0), 10);
  var firstTwoDigits = parseInt(sterilized.substring(0, 2), 10);
  var isInvalidMonth = firstDigit > 1 || firstTwoDigits > 12;
  var result = isInvalidMonth ? "0".concat(sterilized) : sterilized;
  return {
    meta: _objectSpread(_objectSpread({}, meta), {}, {
      didZeroPad: isInvalidMonth
    }),
    value: _objectSpread(_objectSpread({}, value), {}, {
      sterilized: result
    })
  };
};

var getDateChangeHandlerMetadata = _ref2 => {
  var {
    sterilizedDate,
    validationFn,
    props
  } = _ref2;
  var hasMonth = sterilizedDate.length > 1;
  var hasYear = sterilizedDate.length > 3;
  return {
    isMaxLength: hasYear,
    month: hasMonth ? sterilizedDate.substring(0, 2) : '',
    validation: validationFn(sterilizedDate, {
      props
    }),
    year: hasYear ? "20".concat(sterilizedDate.substring(2)) : ''
  };
};

var getDateMetadata = value => {
  var {
    previous,
    raw,
    sanitized,
    sterilized
  } = value;
  var {
    char,
    index
  } = getInsertionLocation(previous, raw);
  var isInsertionAtMask = raw.length > previous.length && Boolean(index) && index > 1 && index < 6 && NON_DIGITS.test(char);
  return {
    meta: {
      didUserEnterInvalidChar: sanitized.length !== raw.length,
      didUserEnterMaskChar: sterilized.length > 1 && isInsertionAtMask && DATE_MASK_USER_INPUT_REGEX.test(raw),
      hasFullMask: DATE_FULL_MASK_REGEX.test(sanitized),
      hasSomeMaskChars: DATE_MASK_CHARS_REGEX.test(sanitized),
      isInsertionAtMask
    },
    value
  };
};

var getInsertionLocation = function getInsertionLocation() {
  var prevValue = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  var newValue = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
  var index = 0;

  while (index < newValue.length) {
    if (prevValue[index] !== newValue[index]) {
      return {
        char: newValue[index],
        index
      };
    }

    index++;
  }

  return {};
};

var getInputAction = _ref3 => {
  var {
    didZeroPad,
    newValue,
    prevValue
  } = _ref3;
  var prevValueLen = prevValue.length;
  var newValueLen = newValue.length;

  if (prevValueLen > newValueLen) {
    return -1;
  }

  var diff = newValueLen - prevValueLen;
  var isSameLength = !diff;
  var isValueSame = newValue === prevValue;

  if (!isSameLength && (!didZeroPad || diff > 1) || isSameLength && !isValueSame && !didZeroPad) {
    return 1;
  }

  return 0;
};

var getSelection = _ref4 => {
  var {
    meta,
    inputAction,
    selectionStart,
    value
  } = _ref4;
  var {
    raw: rawValue,
    sterilized: sterilizedValue
  } = value;
  var userDeletedThirdDigit = selectionStart === 5 && sterilizedValue.length >= 2 && inputAction < 0 && meta.hasFullMask;
  var isInvalidCharInsertionAtMask = meta.didUserEnterInvalidChar && meta.isInsertionAtMask;

  if (userDeletedThirdDigit || meta.didUserEnterMaskChar || isInvalidCharInsertionAtMask) {
    return {
      end: 5,
      start: 5
    };
  }

  var {
    didZeroPad,
    hasSomeMaskChars
  } = meta;
  var invalidCharsBeforeStart = rawValue.substring(0, selectionStart).match(NON_DIGITS_GLOBAL) || [];
  var sanitizedSelectionStart = selectionStart - invalidCharsBeforeStart.length;
  var zeroPadAdjustment = didZeroPad ? 1 : 0;
  var needsMaskAdjustment = sanitizedSelectionStart > 2 || sanitizedSelectionStart === 2 && (!hasSomeMaskChars || didZeroPad) || rawValue.length === 1 && inputAction > 0 && didZeroPad || sterilizedValue.length === 2 && inputAction > 0;
  var maskAdjustment = needsMaskAdjustment ? 3 : 0;
  var finalSelection = sanitizedSelectionStart + zeroPadAdjustment + maskAdjustment;
  return {
    end: finalSelection,
    start: finalSelection
  };
};

var maskDate = dateString => {
  if (dateString.length < 2) {
    return dateString;
  }

  var month = dateString.substring(0, 2);
  var year = dateString.substring(2, 4);
  return "".concat(month, " / ").concat(year);
};

var processDateChange = _ref5 => {
  var {
    event,
    newValue,
    prevValue,
    props,
    validationFn
  } = _ref5;
  var {
    target
  } = event;
  var processedDate = pipe({
    newValue: newValue || target.value,
    prevValue
  }, sanitizeDateString, getDateMetadata, conditionallyZeroPadValue, removeExcessDigits);
  var inputAction = getInputAction({
    didZeroPad: processedDate.meta.didZeroPad,
    newValue: processedDate.value.sterilized,
    prevValue: stripNonDigits(prevValue)
  });

  var inputData = _objectSpread(_objectSpread({}, processedDate), {}, {
    inputAction,
    selectionStart: target.selectionStart
  });

  var selectionRange = getSelection(inputData);
  var maskedValue = conditionallyMaskDate(inputData);
  var {
    sterilized: sterilizedDate
  } = processedDate.value;
  var meta = getDateChangeHandlerMetadata({
    props,
    sterilizedDate,
    validationFn
  });
  return {
    changeHandlerArgs: [event, maskedValue, prevValue, meta],
    selectionRange
  };
};

var removeExcessDigits = _ref6 => {
  var {
    value
  } = _ref6,
      rest = _objectWithoutProperties(_ref6, _excluded);

  var {
    sterilized
  } = value;
  var month = sterilized.substring(0, 2);
  var isFourDigitYear = /19|20/.test(sterilized.substring(2, 4)) && sterilized.length === 6;
  var yearArgs = isFourDigitYear ? [4] : [2, 4];
  var year = sterilized.substring(...yearArgs);
  return _objectSpread(_objectSpread({}, rest), {}, {
    value: _objectSpread(_objectSpread({}, value), {}, {
      sterilized: "".concat(month).concat(year)
    })
  });
};

var sanitizeDateString = _ref7 => {
  var {
    newValue,
    prevValue
  } = _ref7;
  var sanitizedValue = newValue.replace(SANITIZE_DATE_REGEX, '');
  var sterilizedValue = stripNonDigits(sanitizedValue);
  return {
    previous: prevValue,
    raw: newValue,
    sanitized: sanitizedValue,
    sterilized: sterilizedValue
  };
};

export { conditionallyMaskDate, conditionallyZeroPadValue, getDateChangeHandlerMetadata, getDateMetadata, getInsertionLocation, getInputAction, getSelection, maskDate, processDateChange, removeExcessDigits, sanitizeDateString };